/* eslint-disable indent */
import * as React from 'react';
import { uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { blueGrey } from '@mui/material/colors';
import {
  Box,
  Button,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  Group,
  Person,
  Star,
} from '@mui/icons-material';

import { styled } from '@mui/styles';
import User from '../../User';
import NewUserModal from './NewUserModal';
import { getAssignmentsFromTaskRawAssignment } from '../../../utils/barnBoardHelper';
import { getHorseAccess } from '../../../redux/horseAccess/actions';
import { RESET_HORSE_ACCESS } from '../../../redux/horseAccess/types';
import Horse from '../../Horse';
import { primary } from '../../../utils/brandColors';
import { setSelectedBarnHorse } from '../../../redux/app/actions';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: blueGrey[50],
  zIndex: 1000,
}));

const GroupItems = styled('ul')({ padding: 0 });

const defaultEnterKey = {
  altKey: false,
  bubbles: true,
  cancelBubble: false,
  cancelable: true,
  charCode: 0,
  code: 'Enter',
  composed: true,
  ctrlKey: false,
  currentTarget: null,
  defaultPrevented: true,
  detail: 0,
  eventPhase: 0,
  isComposing: false,
  isTrusted: true,
  key: 'Enter',
  keyCode: 13,
  location: 0,
  metaKey: false,
  repeat: false,
  returnValue: false,
  shiftKey: false,
  type: 'keydown',
  which: 13,
}

const BarnUsersAutocomplete = ({
  id,
  freeSolo,
  onSelect,
  value,
  validation,
  multiple,
  label,
  limitTags,
  size,
  disabled,
  blurOnSelect,
  showAdornment,
  suggestAssignmentHorseTagId,
  suggestParticipantHorseTagId,
  clientAccessHorseId,
}) => {
  const dispatch = useDispatch();

  // React state
  const [search, setSearch] = React.useState('');

  // New User Modal
  const [openAddUserModal, setOpenAddUserModal] = React.useState(false);
  const [originalEvent, setOriginalEvent] = React.useState({});
  const [originalValue, setOriginalValue] = React.useState('');
  const [originalBarnUsers, setOriginalBarnUsers] = React.useState([]);
  const [recentlyUsed, setRecentlyUsed] = React.useState([]);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [suggestedUsers, setSuggestedUsers] = React.useState([]);

  // Redux state
  const selectedBarn = useSelector((state) => state.barn.selectedBarn)
  const barnUsers = useSelector((state) => state.barn.barnUsers);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const permissions = useSelector((state) => state.user.permissions);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const mappedHorseSuggestions = useSelector((state) => state.board.mappedHorseSuggestions);
  const horseAccess = useSelector((state) => state.horseAccess.horseAccess.content);

  React.useEffect(() => {
    if ((suggestAssignmentHorseTagId || suggestParticipantHorseTagId) && mappedHorseSuggestions
      && mappedHorseSuggestions[suggestAssignmentHorseTagId || suggestParticipantHorseTagId]) {
      const suggestion = mappedHorseSuggestions[suggestAssignmentHorseTagId || suggestParticipantHorseTagId];
      if (suggestAssignmentHorseTagId) {
        setSuggestedUsers(Object.keys(suggestion.byAssignment).map((assignment) => getAssignmentsFromTaskRawAssignment(assignment, barnUsersMap))
          .map(((assignments) => assignments && assignments.length === 1 && assignments[0])))
      } else if (suggestParticipantHorseTagId) {
        setSuggestedUsers(Object.keys(suggestion.byParticipant).map((participant) => getAssignmentsFromTaskRawAssignment(participant, barnUsersMap))
          .map(((participants) => participants && participants.length === 1 && participants[0])))
      }
    } else {
      setSuggestedUsers([])
    }
  }, [suggestAssignmentHorseTagId, suggestParticipantHorseTagId, mappedHorseSuggestions])

  React.useEffect(() => {
    if (clientAccessHorseId) {
      dispatch(getHorseAccess(clientAccessHorseId, 0, 20, [{
        field: 'endDate',
        sort: 'desc',
      }].map((s) => `&sort=${s.field},${s.sort}`)));
    } else {
      dispatch({ type: RESET_HORSE_ACCESS });
    }
  }, [clientAccessHorseId]);

  React.useEffect(() => {
    if (selectedBarn && window.localStorage.getItem(`recently-used-barn-users-${selectedBarn.tenant}`) && JSON.parse(window.localStorage.getItem(`recently-used-barn-users-${selectedBarn.tenant}`)).length > 0) {
      setRecentlyUsed(JSON.parse(window.localStorage.getItem(`recently-used-barn-users-${selectedBarn.tenant}`)))
    } else {
      setRecentlyUsed([])
    }
  }, [value])

  const handleChangeBarnUsers = (e, newBarnUsers) => {
    if (multiple) {
      let updatedRecentlyUsed = recentlyUsed || [];
      updatedRecentlyUsed = uniqBy([...newBarnUsers, ...updatedRecentlyUsed], 'userFullName').splice(0, 5) // Keep 5 most recent
      setRecentlyUsed(updatedRecentlyUsed)
      window.localStorage.setItem(`recently-used-barn-users-${selectedBarn.tenant}`, JSON.stringify(updatedRecentlyUsed))
    }
    onSelect(e, newBarnUsers)
  }

  const handleSelect = (e, newBarnUsers) => {
    setSearch('')
    if (freeSolo && multiple && e.target.value && e.target.value.length > 0
      && (permissions.owner || permissions.admin) && !(newBarnUsers && newBarnUsers[0] && newBarnUsers[0].id)
      && !newBarnUsers[newBarnUsers.length - 1].id && newBarnUsers.length > value.length) {
      // We get here is a freesolo user is added (just text no selected barn user) Open modal and prompt for new user
      setOriginalValue(e.target.value);
      setOriginalEvent(e);
      setOriginalBarnUsers(newBarnUsers.map((bu) => (bu.userFullName
        ? bu
        : { userFullName: bu })));
      const names = e.target.value.split(' ');
      setFirstName((names && names[0]) || '');
      setLastName((names && names[1]) || '');
      setOpenAddUserModal(true);
    } else if (freeSolo && multiple && e.target.value && e.target.value.length > 0
      && !(permissions.owner || permissions.admin) && !(newBarnUsers && newBarnUsers[0] && newBarnUsers[0].id)
      && !newBarnUsers[newBarnUsers.length - 1].id) {
      // Non staff and new user is free solo so just add it
      handleChangeBarnUsers(e, newBarnUsers.map((bu) => (bu.userFullName
        ? bu
        : { userFullName: bu })));
    } else {
      // selected a user or invalid admin permissions so move forward
      handleChangeBarnUsers(e, newBarnUsers);
    }
  }

  const handleCloseModal = (newUser) => {
    if (!newUser) {
      handleChangeBarnUsers(originalEvent, originalBarnUsers);
    } else {
      // first the original user in barn users list and remove, add newly created user
      handleChangeBarnUsers(originalEvent, [newUser, ...originalBarnUsers.filter((bu) => bu.userFullName !== originalValue)]);
    }
    setOpenAddUserModal(false);
    setOriginalValue('');
    setOriginalEvent({});
    setOriginalBarnUsers([]);
    setFirstName('');
    setLastName('');
  }

  const finalOptions = [...uniqBy(horseAccess.map(((ha) => ({
    ...ha.user,
    ru: 'People',
    key: `access-${ha.user.id}`,
  }))), 'id').filter((bu) => bu.userFullName.toLowerCase().indexOf(search.toLowerCase()) >= 0),
  ...suggestedUsers.map(((bu) => ({
    ...bu,
    ru: 'Questri Suggestions',
    key: `suggestion-${bu.id}`,
  }))).filter((bu) => bu.userFullName.toLowerCase().indexOf(search.toLowerCase()) >= 0),
  ...((recentlyUsed || []).filter((bu) => bu.userFullName).map((ru) => ({
    ...((barnUsersMap[ru.id || ru.auth0UserId || ru.email || ru.userFullName]
      && barnUsersMap[ru.id || ru.auth0UserId || ru.email || ru.userFullName][0]) || ru),
    ru: 'Recently Used',
    key: `recent-${ru.id || ru.userFullName}`,
  })).filter((bu) => bu.userFullName.toLowerCase().indexOf(search.toLowerCase()) >= 0)
    .sort((a, b) => -b.userFullName.localeCompare(a.userFullName))),
  ...(barnUsers.map((bu) => ({
    ...bu,
    ru: 'All Users',
    key: `user-${bu.id}`,
  })))];

  return (
    <>
      <Autocomplete
        ListboxProps={{ onClick: (e) => e.stopPropagation() }}
        id={id}
        blurOnSelect={blurOnSelect || !multiple}
        disabled={disabled}
        freeSolo={freeSolo}
        multiple={multiple}
        onChange={handleSelect}
        limitTags={limitTags}
        size={size}
        noOptionsText="Press the 'Enter' key to create a new user"
        clearOnBlur
        sx={{ width: '100%' }}
        options={finalOptions}
        value={value}
        groupBy={(option) => option.ru}
        isOptionEqualToValue={(option, valueCompare) => (valueCompare.id
          ? valueCompare.id === option.key
          : valueCompare.userFullName === option.userFullName)}
        getOptionLabel={(option) => ((option && option.id)
          ? `${option.userFullName} (${option.email || 'No Email'})`
          : '')}
        getOptionKey={(option) => `${option.key}-${option.ru}`}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>
              <Box
                sx={{
                  display: 'flex',
                  alignItem: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItem: 'center',
                  }}
                >
                  {params.group === 'People' && clientAccessHorseId && barnHorsesMap[clientAccessHorseId] && <Horse chip horse={barnHorsesMap[clientAccessHorseId][0].horse} />}
                  {params.group === 'Questri Suggestions' && <AutoAwesome sx={{ color: '#FF6600 !important' }} />}
                  {params.group === 'Recently Used' && <Star color="primary" />}
                  <Box sx={{ ml: 0.5 }}>{params.group}</Box>
                </Box>

                {params.group === 'People' && clientAccessHorseId && barnHorsesMap[clientAccessHorseId] && <Button size="small" variant="outlined" startIcon={<Group />} onClick={() => { dispatch(setSelectedBarnHorse(barnHorsesMap[clientAccessHorseId][0])); }}>Manage</Button>}
              </Box>
            </GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            style={{
              paddingLeft: '4px',
              width: '100%',
              key: `barn-users-autocomplete-li-${option.key}-${option.ru}`,
              backgroundColor: selected
                ? primary[100]
                : 'inherit',
              border: selected
                ? `2px solid ${primary[500]}`
                : undefined,
            }}
          >
            <User
              key={`barn-users-autocomplete-user-${option.key}--${option.ru}`}
              user={option}
              secondary={option.email || null}
              showAccountIndicator
            />
          </li>
        )}
        renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
          <User
            chip
            {...getTagProps({ index })}
            key={`barn-users-autocomplete-chip-${option.id || option.userFullName}`}
            user={option}
          />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            fullWidth
            onChange={(e) => { setSearch(e.target.value); }}
            onBlur={(e) => {
              // If they didn't press enter and there is a value, let's press enter so it gets added
              if (search && search.length > 0) {
                const ev = new KeyboardEvent('keydown', defaultEnterKey);
                e.target.dispatchEvent(ev);
              } setSearch('');
            }}
            error={!!validation}
            helperText={validation && validation}
            InputProps={showAdornment
              ? {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  finalOptions.length === 0 && search.length > 0 && (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: '12px' }}>Press ENTER to create user</Typography>
                    </InputAdornment>
                  )
                ),
                onClick: (e) => e.stopPropagation(),
                onFocus: (e) => e.stopPropagation(), // Added with update
              }
              : params.InputProps}
            disabled={disabled}
          />
        )}
      />
      <NewUserModal open={openAddUserModal} onNewUser={handleCloseModal} onCancel={handleCloseModal} firstName={firstName} lastName={lastName} />
    </>
  );
};

BarnUsersAutocomplete.propTypes = {
  id: PropTypes.string,
  freeSolo: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.any,
  validation: PropTypes.string,
  multiple: PropTypes.bool,
  size: PropTypes.string,
  limitTags: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  blurOnSelect: PropTypes.bool,
  showAdornment: PropTypes.bool,
  suggestAssignmentHorseTagId: PropTypes.string,
  suggestParticipantHorseTagId: PropTypes.string,
  clientAccessHorseId: PropTypes.any,
};

BarnUsersAutocomplete.defaultProps = {
  id: 'barn-users-select-autocomplete',
  freeSolo: true,
  value: null,
  validation: null,
  multiple: true,
  size: 'small',
  limitTags: 5,
  label: 'Users',
  disabled: false,
  blurOnSelect: false,
  showAdornment: false,
  suggestAssignmentHorseTagId: null,
  suggestParticipantHorseTagId: null,
  clientAccessHorseId: null,
};

export default BarnUsersAutocomplete;
